import {ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {DialogService} from '@app/services/dialog.service';
import {GtManageOrganizationService} from '@app/services/gt-manage-organization.service';
import {GtNotificationService} from '@app/services/gt-notification.service';
import {MediaFileControllerService} from '@app/services/media-file-controller.service';
import {MediaFileStoreModel} from '@app/services/media-file-store.service';
import {environment} from '@environment';
import {FileItem, FileUploader} from 'ng2-file-upload';
import {EMPTY, Observable, of} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';

@Component({
  templateUrl: './announcements-table-dialog.component.html',
  styleUrls: ['./announcements-table-dialog.component.scss']
})
export class AnnouncementsTableDialogComponent implements OnInit {
  uploader: FileUploader;
  audio = new Audio();
  playing$ = this.mediaFileControllerService.playing$;
  @ViewChild('fileInput', {static: true})
  fileInputField: ElementRef;

  mediaFile: MediaFileStoreModel | null;
  fileName = '';
  announcementForm = new UntypedFormGroup({
    displayName: new UntypedFormControl('', [Validators.required]),
    description: new UntypedFormControl(''),
    file: new UntypedFormControl(null)
  });

  constructor(
    private dialogRef: MatDialogRef<AnnouncementsTableDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: MediaFileStoreModel,
    private cdr: ChangeDetectorRef,
    private mediaFileControllerService: MediaFileControllerService,
    private manageOrganizationService: GtManageOrganizationService,
    private dialogService: DialogService,
    private notificationService: GtNotificationService
  ) {
    this.mediaFile = data ? {...data} : null;
  }

  ngOnInit() {
    if (this.mediaFile) {
      this.announcementForm.get('displayName').setValue(this.mediaFile.displayName);
      this.announcementForm.get('description').setValue(this.mediaFile.description);
      this.fileName = this.mediaFile.fileName.split('/').pop();
    }

    this.initiateUploader();
    this.audio.onended = () => {
      this.cdr.markForCheck();
    };
  }

  togglePlayAudio() {
    if (!this.audio.paused) {
      this.audio.pause();
    } else {
      const file = this.uploader.queue[0].file;
      const blob = new Blob([file.rawFile], { type: file.type });
      this.audio.src = URL.createObjectURL(blob);
      this.audio.load();
      this.audio.play();
    }
  }

  play() {
    this.mediaFileControllerService.play(this.mediaFile.fileName);
  }

  stop() {
    this.mediaFileControllerService.stop();
  }

  clearFile() {
    this.uploader.clearQueue();
    this.fileName = '';
  }

  onConfirm() {
    if (!this.mediaFile) {
      this.uploader.uploadAll();
    } else {
      this.updateAnnouncement(this.mediaFile).subscribe();
    }
  }

  private initiateUploader() {
    const uploadUrl = `${environment.apiUrl.replace('rest', '')}upload`;
    this.uploader = new FileUploader({
      url: uploadUrl,
      itemAlias: 'uploadedFile',
      maxFileSize: 10485760, // 10MB
      headers: [
        {name: 'organization', value: this.manageOrganizationService.manageOname},
        {name: 'login', value: this.manageOrganizationService.manageLogin}
      ]
    });

    this.uploader.onAfterAddingFile = fileItem => {
      this.fileName = fileItem.file.name;
      const displayName = this.fileName.substring(0, this.fileName.lastIndexOf('.'));
      this.announcementForm.get('displayName').setValue(displayName);
      // if this is empty, VCC will show the filename with path and all instead
      this.announcementForm.get('description').setValue(displayName);
    }
    this.uploader.onSuccessItem = (item: FileItem, response: string) => {
      const resp = JSON.parse(response);
      if (resp.code === 200) {
        this.mediaFileControllerService.reload();
        this.closeDialog(true);
      } else {
        this.notificationService.showErrorCustomMessage('announcement.error_saving');
        this.uploader.queue[0].isUploaded = false;
      }
    };
    this.uploader.onErrorItem = (item: FileItem, response: string, status: number) => {
      this.uploader.queue[0].isUploaded = false;
      if (status === 413) {
        this.notificationService.showErrorCustomMessage('announcement.error_file_too_big');
      } else {
        this.notificationService.showErrorMessage(response);
      }
    };
    this.uploader.onWhenAddingFileFailed = (item, failingFilter, options) => {
      switch (failingFilter.name) {
        case 'fileSize': {
          this.notificationService.showErrorCustomMessage('announcement.error_file_too_big');
          break;
        }
        default:
          this.notificationService.showErrorCustomMessage('announcement.error_choosing');
      }
      // must clear input to trigger onWhenAddingFileFailed more than once for same file
      // https://github.com/valor-software/ng2-file-upload/issues/871
      this.fileInputField.nativeElement.value = '';
    };
    this.uploader.onBuildItemForm = (fileItem, form) => {
      form.append('displayName' , this.announcementForm.value.displayName);
      form.append('description' , this.announcementForm.value.description);
    };
  }

  private updateAnnouncement(mediaFile: MediaFileStoreModel): Observable<void> {
    if (!mediaFile) {
      return of();
    }

    const form = this.announcementForm.value;
    const updatedMediaFile: MediaFileStoreModel = {
      ...mediaFile,
      displayName: form.displayName,
      description: form.description
    };

    return this.mediaFileControllerService.update(updatedMediaFile).pipe(
      tap(() => {
        this.closeDialog(true);
      }),
      catchError(error => {
        this.notificationService.showErrorMessage(error);
        return EMPTY;
      })
    );
  }

  private closeDialog(success: boolean) {
    this.uploader.clearQueue();
    this.dialogRef.close(success);
  }
}
